/**
 * @file AgencyOfficeDropdown.js
 * @description A dropdown component for selecting an agency office.
 */

import { useState, useEffect } from 'react';
import { fetchAgencyOffices } from '../../../services/systemService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

/**
 * Represents a dropdown component for selecting an agency office.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines if the label should be displayed inline.
 * @param {string} props.selectedOffice - The ID of the selected office.
 * @param {Function} props.onOfficeChange - The callback function to be called when the selected office changes.
 * @param {string} props.agencyId - The ID of the agency.
 * @param {string} [props.label="Office"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines if the dropdown is required.
 * @returns {JSX.Element} The rendered AgencyOfficeDropdown component.
 */
const AgencyOfficeDropdown = ({
    className,
    inlineLabel = true,
    selectedOffice,
    onOfficeChange,
    agencyId,
    label = 'Agency Location',
    isRequired = false,
}) => {
    const [offices, setOffices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getOffices = async () => {
            setIsLoading(true);
            try {
                const data = await fetchAgencyOffices(agencyId);
                setOffices(data);
            } catch (error) {
                console.error('Could not fetch agency locations', error);
                Toast.error(getText('msg_e1c59b27e1a3'));
            } finally {
                setIsLoading(false);
            }
        };

        if (agencyId) {
            getOffices();
        } else {
            setOffices([]);
            setIsLoading(false);
        }
    }, [agencyId]);

    const getDefaultLabel = () => {
        const matchingOffice = offices.find(
            office => office.id === selectedOffice,
        );
        return matchingOffice ? matchingOffice.name : 'Select Location';
    };

    const officeOptions = [
        { value: null, label: getText('msg_9fa73f5b8043') },
        ...offices.map(o => ({ value: o.id, label: o.name })),
    ];

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="office-dropdown">
                {label}
                {isRequired && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="office-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={officeOptions}
                    value={selectedOffice}
                    onChange={onOfficeChange}
                    disabled={offices.length === 0 || isLoading}
                />
            </LoadingWrapper>
        </div>
    );
};

export default AgencyOfficeDropdown;
